import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SwapOutlined, ShopOutlined } from '@ant-design/icons';
import { Button, Card, Space, Typography } from 'antd';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { merchantService } from '@services/operator/merchantService';
import Select from '@components/shared/ui/Select';

interface MerchantInfo {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface MerchantSelectChildrenArgs {
  merchantId: string;
  merchant: MerchantInfo | null | undefined;
  onResetMerchant: () => void;
}

interface MerchantSelectProps {
  children?: ReactNode | ((args: MerchantSelectChildrenArgs) => ReactNode);
  title?: ReactNode | null;
  subtitle?: ReactNode | null;
}

interface FetchMerchantsArgs {
  search?: string;
  sinceId?: string | null;
  pageSize: number;
}

interface FetchMerchantsResult {
  options: Array<{ label: string; value: string; [key: string]: unknown }>;
  hasMore: boolean;
  sinceId: string | null;
}

/**
 * MerchantSelect Component
 *
 * Reusable component for operator merchant selection pattern.
 * Uses paginated API with since_id for infinite scroll loading.
 * Automatically sets selected merchant to OperatorDataContext.
 * When there is only one merchant, it will be automatically selected.
 *
 * @param {object} props
 * @param {function} props.children - Component to render when merchant is selected
 * @param {string} props.title - Custom title for the selection card
 * @param {string} props.subtitle - Custom subtitle for the selection card
 *
 * @example
 * <MerchantSelect>
 *   <ProductEditForm />
 * </MerchantSelect>
 */
const MerchantSelect: React.FC<MerchantSelectProps> = ({
  children,
  title = null,
  subtitle = null
}) => {
  const { t } = useTranslation();
  const { merchantId, merchant, merchants, setCurrentMerchant, resetMerchant, loading } = useOperatorData();
  const autoSelectTriggeredRef = useRef(false);

  // 只有一個 merchant 時自動選擇（不需要顯示選擇 UI）
  const isSingleMerchant = !loading && merchants?.length === 1;
  useEffect(() => {
    if (isSingleMerchant && !merchantId && !autoSelectTriggeredRef.current) {
      autoSelectTriggeredRef.current = true;
      setCurrentMerchant(merchants[0]);
    }
  }, [isSingleMerchant, merchantId, merchants]);

  const handleSelectMerchant = useCallback((m: MerchantInfo | null) => {
    setCurrentMerchant(m);
  }, [setCurrentMerchant]);

  const fetchMerchants = useCallback(async ({ search, sinceId, pageSize }: FetchMerchantsArgs): Promise<FetchMerchantsResult> => {
    try {
      const params: Record<string, unknown> = {
        page_size: pageSize,
      };

      if (sinceId) {
        params.since_id = sinceId;
      }

      if (search) {
        params.search = search;
      }

      const response = await merchantService.getList(params);

      if (response.success) {
        const options = response.data.map((m: MerchantInfo) => ({
          label: m.name,
          value: m.id,
          ...m
        }));

        return {
          options,
          hasMore: response.has_more,
          sinceId: response.since_id
        };
      }
      return { options: [], hasMore: false, sinceId: null };
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
      return { options: [], hasMore: false, sinceId: null };
    }
  }, [handleSelectMerchant]);

  const handleSelect = (selectedMerchant: { value: string; label: string; [key: string]: unknown } | null) => {
    if (selectedMerchant) {
      handleSelectMerchant({
        id: selectedMerchant.value,
        name: selectedMerchant.label,
        ...selectedMerchant
      });
    } else {
      handleSelectMerchant(null);
    }
  };

  // Select 是 .jsx，TS 從其 destructure 推導出的 props 不完整，cast 為 any 保留原行為
  const SelectAny = Select as React.ComponentType<any>;

  // If merchant is selected, render children with merchant bar
  if (merchantId) {
    const content = typeof children === 'function'
      ? children({ merchantId, merchant, onResetMerchant: resetMerchant })
      : children;

    return (
      <>
        {/* 多 merchant 時顯示頂部 bar + 變更按鈕；單一 merchant 不顯示 */}
        {!isSingleMerchant && (
          <div className="operator-merchant-bar">
            <span className="operator-merchant-bar__info">
              <ShopOutlined style={{ color: '#1890ff', fontSize: 14 }} />
              <Typography.Text style={{ fontSize: 13, color: '#434343' }}>
                {t('operator.products.merchant_selection.current_merchant')}
              </Typography.Text>
              <Typography.Text strong style={{ fontSize: 13 }}>
                {merchant?.name}
              </Typography.Text>
            </span>
            <Button
              type="text"
              size="small"
              icon={<SwapOutlined />}
              onClick={resetMerchant}
              style={{ color: '#1890ff', fontSize: 13 }}
            >
              {t('common.button.change')}
            </Button>
          </div>
        )}
        {content}
      </>
    );
  }

  // 資料尚未載入完成，不顯示選擇 UI（避免閃爍）
  if (loading) {
    return null;
  }

  // 單一 merchant 但尚未自動選完，顯示 loading
  if (isSingleMerchant) {
    return null;
  }

  // Otherwise, show merchant selection UI
  return (
    <div style={{ padding: '24px' }}>
      <Card title={title || t('operator.products.merchant_selection.title')}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            {subtitle || t('operator.products.merchant_selection.subtitle')}
          </Typography.Text>

          <SelectAny
            data-testid="merchant-select"
            placeholder={t('operator.products.merchant_selection.instructions')}
            style={{ width: '100%' }}
            onChange={handleSelect}
            fetchData={fetchMerchants}
            pageSize={20}
            searchDebounceMs={300}
            isSearchable
            isLoading={loading}
          />
        </Space>
      </Card>
    </div>
  );
};

export default MerchantSelect;
